<template>
  <sign-in-dialog
    v-if="!isSignedIn"
    :value="value"
    @input="$nextTick(() => isSignedIn || $emit('input', false))"
  />

  <dialog-scroll-lock v-else width="400" :value="value" @input="$emit('input', false)">
    <v-card>
      <v-btn class="ma-3" style="position: absolute; right: 0;" icon @click="$emit('input', false)">
        <v-icon v-html="mdiClose" />
      </v-btn>

      <v-card-title v-if="title" v-html="title" />
      <v-card-subtitle v-if="isSubscriptionUpgrade && !global.profile.org_id && !org">
        This plan includes access to the Ellacard business tools. Please create an organization account to continue.
      </v-card-subtitle>
      <v-card-subtitle v-else-if="subtitle" v-html="subtitle" />
      <v-card-subtitle v-if="isSubscriptionUpgrade && !global.profile.org_id && org">
        <v-btn class="pl-2" small @click="org = null">
          <v-icon class="mr-1" v-html="mdiChevronLeft" /> Organization details
        </v-btn>
      </v-card-subtitle>
      <v-divider v-if="title || subtitle || (isSubscriptionUpgrade && !global.profile.org_id && org)" />

      <template v-if="!isSubscriptionUpgrade || global.profile.org_id || org">
        <v-card-text class="pt-5" style="min-height: 300px;">
          <template v-if="itemized">
            <div class="text-center text-h3 mb-5 py-3">
              <price :value="itemized.price" :per="metadataWithOrg.target == 'SUBSCRIPTION' ? 'year' : null" />
            </div>
            <payment-btns
              class="mx-auto"
              :itemized="itemized"
              :metadata="metadataWithOrg"
              :loading.sync="isPaymentBtnsLoading"
              :progress.sync="isPaymentInProgress"
              @price-update="onPriceUpdate"
              @complete="onComplete"
            />
          </template>
        </v-card-text>
        <template v-if="details">
          <v-divider />
          <v-card-text class="caption font-italic pt-5" v-html="details" />
        </template>
      </template>

      <v-slide-x-transition v-if="isSubscriptionUpgrade">
        <div v-if="!global.profile.org_id" v-show="!org" v-bind:class="{ absolute: org }">
          <v-card-text class="pt-5" style="min-height: 300px;">
            <org-sign-up-form ref="org" hide-email hide-btns />
            <div class="mt-6 text-right">
              <v-btn class="pr-2" color="primary" @click="org = $refs.org.getOrgParams()">
                Continue <v-icon class="ml-1" v-html="mdiChevronRight" />
              </v-btn>
            </div>
          </v-card-text>
        </div>
      </v-slide-x-transition>
    </v-card>
  </dialog-scroll-lock>
</template>


<script>
import OrgSignUpForm from '@/components/OrgSignUpForm.vue';
import PaymentBtns from '@/components/PaymentBtns.vue';
import Price from '@/components/Price.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import { LOCALE } from '@/utils/intl.js';
import { mdiChevronLeft, mdiChevronRight, mdiClose } from '@mdi/js';


export default {
  name: 'place-order-btn',

  components: {
    OrgSignUpForm,
    PaymentBtns,
    Price,
    SignInDialog
  },

  props: [ 'value', 'metadata', 'title', 'subtitle', 'details' ],

  data() {
    return {
      showSignIn: false,
      itemized: null,
      org: undefined,
      isPaymentBtnsLoading: false,
      isPaymentInProgress: false,
      loadPriceStatus: this.$track('loadPrice'),
      placeOrderStatus: this.$track('placeOrder')
    };
  },

  computed: {
    url() {
      return location.href;
    },

    isSubscriptionUpgrade() {
      return this.metadata.target == 'SUBSCRIPTION-UPGRADE';
    },

    metadataWithOrg() {
      return { ...this.metadata, org: this.org };
    },

    loadPriceParams() {
      // Note that 'user_id' is not actually a param of get-itemized-price, but we also use this computed value as a
      // trigger to reload the price
      if (this.isSubscriptionUpgrade && !this.global.profile.org_id && !this.org)
        return null;
      return { user_id: this.globalUserId, metadata: this.metadataWithOrg, locale: LOCALE };
    },

    isLoading() {
      return this.loadPriceStatus.pending || this.isPaymentBtnsLoading;
    }
  },

  created() {
    Object.assign(this, { mdiChevronLeft, mdiChevronRight, mdiClose });
  },

  methods: {
    async loadPrice() {
      if (!this.loadPriceParams) {
        this.itemized = null;
        return;
      }

      try {
        this.itemized = await this.$call('get-itemized-price', this.loadPriceParams);
        this.$emit('loaded');
      } catch (e) {
        this.$emit('price-error', e);
      }
    },

    onPriceUpdate() {
      this.$confirm({
        title: 'Items have been updated',
        msg: 'These items have changed since last loaded',
        buttons: [ 'Ok' ],
        primary: 'Ok'
      });
      this.loadPrice();
    },

    async placeOrder() {
      if (this.itemized.price != '0.00') {
        this.showPayment = true;
        return;
      }
      const params = { metadata: this.metadataWithOrg, locale: LOCALE, items: this.itemized.items };
      const response = await this.$call('fulfill-no-payment', params);
      if (response.price_update)
        this.onPriceUpdate();
      else
        this.onComplete(response);
    },

    async onComplete(payment_id) {
      // Note that 'payment_id' may be undefined if we failed the idempotency check
      if (this.isSubscriptionUpgrade) {
        await this.$refreshJWT();
        this.$notify('Subscription upgraded');
      }
      this.$emit('complete', payment_id, this.metadataWithOrg, this.itemized);
      $event.log('checkout', { metadata: this.metadataWithOrg, ...this.itemized, payment_id });
    }
  },

  watch: {
    value: {
      immediate: true,
      handler() {
        this.org = undefined;
      }
    },

    loadPriceParams: {
      deep: true,
      immediate: true,
      handler(v) {
        if (v)
          this.loadPrice();
      }
    },

    isLoading: {
      immediate: true,
      handler(v) {
        this.$emit('update:loading', v);
      }
    },

    // isPaymentBtnsLoading(v) {
    //   if (!v && !this.didLoad) {
    //     this.didLoad = true;
    //     this.$emit('loaded');
    //   }
    // },
  }
}
</script>
